import { ChartData } from "chart.js";
import { ShiftStatusEnum } from "~/graphql/__generated__/graphql";
import { StatusPalate, visitResourceStatus } from "~/utils/statuses";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { LocalCalendarBlock, LocalShift, LocalVisit } from "~/hooks/visitDragDropReducer";
import { calculateCancellationRate, calculateProviderUtilization } from "@caredna-marlin/utils/kpisHelper";
import { getShiftCalendarBlockOverlapMinutes } from "./useUtilizationPerShift";

interface ChartDataResult {
  chartData: ChartData<"doughnut">;
  cancellationChartData: ChartData<"doughnut">;
  visitChartData: ChartData<"doughnut">;
  totalVisits: number;
  providerUtilization: string | number;
  cancellationRate: string | number;
}

/**
 * A custom hook that calculates and prepares data for various charts based on the provided `combinedData`.
 *
 * @param combinedData - The combined data from GraphQL queries.
 * @returns An object containing the chart data, utilization per provider, and other calculated values.
 */
const useChartData = (
  visits: LocalVisit[],
  localShifts: LocalShift[],
  calendarBlocks: LocalCalendarBlock[]
): ChartDataResult => {
  const orgConfig = useOrgConfig();
  let totalShiftTime = 0;
  let totalVisitTime = 0;
  let totalDriveTime = 0;
  let totalVisits = 0;
  let canceledVisits = 0;
  let totalBreakTime = 0;

  const cancellationReasonCount: { [key: string]: number } = {};
  const visitTypeCount: { [key: string]: number } = {};

  if (visits.length > 0 && localShifts.length > 0) {
    if (localShifts.length > 0) {
      localShifts.forEach((shift) => {
        if (shift.status === ShiftStatusEnum["Canceled"]) { return; }

        totalBreakTime += getShiftCalendarBlockOverlapMinutes(shift, calendarBlocks);

        const startTime = new Date(shift.startTime).getTime();
        const endTime = new Date(shift.endTime).getTime();
        totalShiftTime += (endTime - startTime) / 60000; // Convert to minutes
      });
    }

    if (visits.length > 0) {
      visits.forEach((visit) => {
        if (visit.eventStatus === "canceled") {
          canceledVisits += 1;
          if (visit.cancellationReason) {
            const reasonDescription = visit.cancellationReason.description;
            if (reasonDescription) {
              cancellationReasonCount[reasonDescription] =
                (cancellationReasonCount[reasonDescription] || 0) + 1;
            }
          }
        } else {
          totalVisits += 1;
          visit.visitResources.forEach((resource) => {
            if (visitResourceStatus({ ...resource, visit }) === "canceled") return;

            const visitStartTime = new Date(resource.startTime).getTime();
            const visitEndTime = new Date(resource.endTime).getTime();

            totalVisitTime += (visitEndTime - visitStartTime) / 60000;

            totalDriveTime +=
            resource.startOfShiftDriveTime + resource.interVisitDriveTime + resource.endOfShiftDriveTime;
          });

          const visitType = orgConfig.visitTypes[visit.visitTypeId]?.name;
          if (visitType) {
            visitTypeCount[visitType] = (visitTypeCount[visitType] || 0) + 1;
          }
        }
      });
    }
  }

  const totalIdleTime = totalShiftTime - totalVisitTime - totalDriveTime;
  
  const providerUtilization = calculateProviderUtilization({
    shiftDuration: totalShiftTime,
    visitResourcesDuration: totalVisitTime,
    breaksDuration: totalBreakTime,
  });

  const cancellationRate = calculateCancellationRate({
    totalVisits: visits.length,
    totalCanceledVisits: canceledVisits
  });

  const cancelReasonLabels = Object.keys(cancellationReasonCount);
  const cancelReasonData = Object.values(cancellationReasonCount);
  const cancellationChartData = {
    labels: cancelReasonLabels,
    datasets: [
      {
        data: cancelReasonData,
        backgroundColor: StatusPalate.canceled,
        hoverBackgroundColor: StatusPalate.canceled,
      },
    ],
  };

  const visitLabelsData = Object.keys(visitTypeCount);
  const visitTypeData = Object.values(visitTypeCount);
  const visitChartData = {
      labels: visitLabelsData,
      datasets: [
        {
          data: visitTypeData,
          backgroundColor: StatusPalate.complete,
          hoverBackgroundColor: StatusPalate.complete,
        },
      ],
    };

  const chartData = {
    labels: ["Visit Time", "Drive Time", "Down Time"],
    datasets: [
      {
        data: [totalVisitTime, totalDriveTime, totalIdleTime],
        backgroundColor: StatusPalate.scheduled,
        hoverBackgroundColor: StatusPalate.scheduled,
      },
    ],
  };

  return {
    chartData,
    providerUtilization,
    visitChartData,
    totalVisits,
    cancellationChartData,
    cancellationRate,
  };
};

export default useChartData;
